import * as fs from 'node:fs';
import * as path from 'node:path';
import type { LogFn } from '../../log.js';

/** Individual files to mirror from bridge parent dirs into session worktree placeholders. */
export const BRIDGE_FILES_ALONG_WORKTREE_PATH = ['AGENTS.md'] as const;

/**
 * When a repo lives under nested folders in the bridge, parent directories in the session
 * worktree are empty placeholders (not git checkouts). Copy configured bridge files from each
 * corresponding parent so agents still see folder-level guidance. Skips the repo directory
 * itself — that path is populated by `git worktree add`.
 */
export function copyBridgeFilesAlongWorktreePath(options: {
  bridgeRoot: string;
  scopeDir: string;
  repoPathRelativeToBridgeRoot: string;
  fileNames?: readonly string[];
  log?: LogFn;
}): void {
  const { bridgeRoot, scopeDir, repoPathRelativeToBridgeRoot, log } = options;
  const fileNames = options.fileNames ?? BRIDGE_FILES_ALONG_WORKTREE_PATH;
  if (repoPathRelativeToBridgeRoot === '.') return;

  const segments = repoPathRelativeToBridgeRoot.split('/').filter(Boolean);
  for (let i = 0; i < segments.length; i++) {
    const prefix = segments.slice(0, i).join('/');
    const bridgeDir = prefix ? path.join(bridgeRoot, prefix) : bridgeRoot;
    const destDir = prefix ? path.join(scopeDir, prefix) : scopeDir;
    for (const fileName of fileNames) {
      copyFileIfPresent(bridgeDir, destDir, fileName, log);
    }
  }
}

function copyFileIfPresent(srcDir: string, destDir: string, fileName: string, log?: LogFn): void {
  const src = path.join(srcDir, fileName);
  if (!fs.existsSync(src)) return;
  const dest = path.join(destDir, fileName);
  try {
    fs.copyFileSync(src, dest);
    log?.(`[worktrees] Copied ${fileName} to ${dest}`);
  } catch (e) {
    log?.(
      `[worktrees] Failed to copy ${fileName} to ${dest}: ${e instanceof Error ? e.message : String(e)}`,
    );
  }
}
